import { Link } from "react-router-dom";
import {
  Wallet,
  ArrowDownLeft,
  ArrowUpRight,
  Receipt,
  Target,
  PiggyBank,
  TrendingUp,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";
import { formatMoney } from "@/lib/currency";

/**
 * /finance — domain hub. Five cards (Accounts / Transactions / Budget /
 * Receipts / Savings) plus a small "this month" cashflow strip.
 *
 * Loading is shallow — no joins. Each sub-route fetches its own detail.
 */

const TILES: { to: string; title: string; subtitle: string; icon: typeof Wallet }[] = [
  { to: "/finance/accounts", title: "Accounts", subtitle: "Balances · multi-currency", icon: Wallet },
  { to: "/finance/transactions", title: "Transactions", subtitle: "Income, expenses, log", icon: ArrowDownLeft },
  { to: "/finance/budget", title: "Budget", subtitle: "Monthly target vs actual", icon: Target },
  { to: "/finance/receipts", title: "Receipts", subtitle: "OCR · Pantry restock", icon: Receipt },
  { to: "/finance/savings", title: "Savings goals", subtitle: "Boston deposit, emergency, etc.", icon: PiggyBank },
  { to: "/finance/investments", title: "Investments", subtitle: "IBKR · VIAC · Schwab", icon: TrendingUp },
];

export function Finance() {
  const { session } = useSession();
  const userId = session?.user?.id;

  const cashflow = useQuery({
    queryKey: ["finance_cashflow_month", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const start = new Date();
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      const since = start.toISOString();
      const { data, error } = await (supabase as never as { from: (t: string) => { select: (s: string) => { eq: (k: string, v: string) => { gte: (k: string, v: string) => Promise<{ data: { amount_base: number | null }[] | null; error: unknown }> } } } })
        .from("finance_transaction")
        .select("amount_base")
        .eq("user_id", userId!)
        .gte("ts", since);
      if (error) throw error;
      const rows = (data ?? []) as { amount_base: number | null }[];
      let income = 0;
      let expense = 0;
      for (const r of rows) {
        const a = Number(r.amount_base ?? 0);
        if (a > 0) income += a;
        else expense += Math.abs(a);
      }
      return { income, expense, net: income - expense };
    },
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: "var(--space-4)",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: "var(--type-display-s)",
            }}
          >
            Finance
          </h1>
          <p style={{ margin: 0, color: "var(--fg-2)", fontSize: "var(--type-body-s)" }}>
            Multi-currency cashflow, budgets, receipts, savings goals.
          </p>
        </div>
        <Link
          to="/finance/transactions/new"
          style={{
            background: "var(--accent)",
            color: "var(--accent-on)",
            padding: "10px 16px",
            borderRadius: 10,
            fontSize: "var(--type-body-s)",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          + Log transaction
        </Link>
      </header>

      <article
        aria-label="This month"
        style={{
          padding: "var(--space-4)",
          borderRadius: "var(--radius-card)",
          border: "1px solid var(--hair)",
          background: "var(--bg-2)",
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "var(--space-4)",
        }}
      >
        <CashflowCell
          label="Income"
          icon={ArrowDownLeft}
          value={formatMoney(cashflow.data?.income ?? 0, "CHF")}
          tone="ok"
        />
        <CashflowCell
          label="Expenses"
          icon={ArrowUpRight}
          value={formatMoney(cashflow.data?.expense ?? 0, "CHF")}
          tone="warn"
        />
        <CashflowCell
          label="Net"
          icon={Wallet}
          value={formatMoney(cashflow.data?.net ?? 0, "CHF", { sign: "auto" })}
          tone={cashflow.data && cashflow.data.net >= 0 ? "ok" : "bad"}
        />
      </article>

      <section
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: "var(--space-4)",
        }}
      >
        {TILES.map((t) => (
          <Link
            key={t.to}
            to={t.to}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-2)",
              padding: "var(--space-4)",
              border: "1px solid var(--hair)",
              borderRadius: "var(--radius-card)",
              background: "var(--bg-2)",
              color: "var(--fg)",
              textDecoration: "none",
              transition: "transform 120ms ease",
            }}
          >
            <t.icon size={22} strokeWidth={1.5} color="var(--accent)" aria-hidden />
            <strong style={{ fontWeight: 500 }}>{t.title}</strong>
            <span style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>{t.subtitle}</span>
          </Link>
        ))}
      </section>
    </div>
  );
}

function CashflowCell({
  label,
  icon: Icon,
  value,
  tone,
}: {
  label: string;
  icon: typeof Wallet;
  value: string;
  tone: "ok" | "warn" | "bad";
}) {
  const color =
    tone === "ok" ? "var(--severity-ok)" : tone === "warn" ? "var(--severity-warn)" : "var(--severity-bad)";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "var(--fg-3)",
          fontSize: 11,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        <Icon size={14} strokeWidth={1.5} />
        {label}
      </div>
      <div className="mono" style={{ fontSize: 22, fontWeight: 500, color: color ?? "var(--fg)" }}>
        {value}
      </div>
    </div>
  );
}
